import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, X, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl } from '../../utils/apiSetup';

const inputClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const getRemarksDisplay = (a) => {
  if (a.status === 'rejected') {
    return a.rejection_reason || '-';
  }
  if (a.status === 'pending') {
    return a.reason || '-';
  }
  return a.hq_remarks || a.checker_remarks || a.maker_remarks || a.reason || '-';
};

const EmployeeLeaves = () => {
  const { user } = useAuth();
  const employeeId = user?.employee_id;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [policyData, setPolicyData] = useState(null);
  const [periodBalances, setPeriodBalances] = useState([]);
  const [applications, setApplications] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [form, setForm] = useState({ leave_type_id: '', from_date: '', to_date: '', reason: '', is_half_day: false, half_day_type: 'first_half' });

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  }), []);

  const loadData = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const year = new Date().getFullYear();
      const [balRes, appRes, typeRes] = await Promise.all([
        fetch(buildApiUrl(`/api/v1/leave-balances/employee/${employeeId}?year=${year}`), { headers }),
        fetch(buildApiUrl(`/api/v1/leaves/applications?employee_id=${employeeId}&size=200`), { headers }),
        fetch(buildApiUrl('/api/v1/leaves/types'), { headers }),
      ]);
      
        if (balRes.ok) {
          const bData = await balRes.json();
          setPolicyData(bData.policy);
          
          const rawBalances = bData.balances || [];
          const leaveTypesWithPeriods = new Set();
          rawBalances.forEach(b => {
            if (b.period_type === 'JAN_JUN' || b.period_type === 'JUL_DEC') {
              leaveTypesWithPeriods.add(b.leave_type_code);
            }
          });

          const unique = [];
          const seen = new Set();
          rawBalances.forEach(b => {
            if (b.period_type === 'ANNUAL' && leaveTypesWithPeriods.has(b.leave_type_code)) {
              return;
            }
            const k = `${b.leave_type_code}_${b.period_type}`;
            if (!seen.has(k)) {
              seen.add(k);
              unique.push(b);
            }
          });
          setPeriodBalances(unique);
        }
      if (appRes.ok) setApplications(await appRes.json());
      if (typeRes.ok) {
        const types = await typeRes.json();
        setLeaveTypes(types);
        setForm((prev) => ({ ...prev, leave_type_id: prev.leave_type_id || types[0]?.id || '' }));
      }
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to load leave module');
    } finally {
      setLoading(false);
    }
  }, [employeeId, headers]);

  useEffect(() => { loadData(); }, [loadData]);

  const submitLeave = async () => {
    if (!form.leave_type_id || !form.from_date || !form.to_date || !form.reason.trim()) return;
    try {
      const start = new Date(form.from_date);
      const end = new Date(form.to_date);
      const days = Math.max(1, ((end - start) / (1000 * 60 * 60 * 24)) + 1);
      const payload = {
        employee_id: employeeId,
        leave_type_id: Number(form.leave_type_id),
        from_date: form.from_date,
        to_date: form.to_date,
        no_of_days: form.is_half_day ? 0.5 : days,
        is_half_day: form.is_half_day,
        half_day_type: form.is_half_day ? form.half_day_type : null,
        reason: form.reason,
      };
      const response = await fetch(buildApiUrl('/api/v1/leaves/applications'), { method: 'POST', headers, body: JSON.stringify(payload) });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to submit leave request');
      }
      setShowApplyModal(false);
      setForm({ leave_type_id: leaveTypes[0]?.id || '', from_date: '', to_date: '', reason: '', is_half_day: false, half_day_type: 'first_half' });
      await loadData();
    } catch (e) {
      setError(e.message || 'Failed to submit leave');
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading leave dashboard...</div>;

  return (
    <div className="p-6 space-y-6 bg-gray-100 min-h-screen">
      {error && <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}
      
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Leave Dashboard</h1>
          <p className="text-gray-600 text-sm">Assigned policy, half-year entitlements & balance ledger</p>
        </div>
        <button onClick={() => setShowApplyModal(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium">
          <Plus className="w-4 h-4" />Apply Leave
        </button>
      </div>

      {/* Policy Card Banner */}
      {policyData && (
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-5 text-white shadow-md flex justify-between items-center">
          <div>
            <div className="flex items-center space-x-2">
              <ShieldCheck className="w-5 h-5 text-blue-200" />
              <h2 className="text-lg font-bold">Assigned Policy: {policyData.name}</h2>
            </div>
            <p className="text-xs text-blue-100 mt-1 font-mono">Policy Code: {policyData.code}</p>
          </div>
          <span className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold uppercase tracking-wider">
            Active Policy
          </span>
        </div>
      )}

      {/* Period Balances Grid */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 space-y-4">
        <h2 className="text-lg font-bold text-gray-900">Period-Wise Entitlements & Balances</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
          {periodBalances.map((b) => (
            <div key={b.id} className="rounded-xl border border-gray-200 p-4 bg-gray-50/50 hover:bg-gray-50 transition">
              <div className="flex justify-between items-start">
                <span className="text-base font-extrabold text-blue-900">{b.leave_type_code}</span>
                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded font-semibold">{b.period_type}</span>
              </div>
              <p className="text-3xl font-black text-gray-900 mt-2">{b.balance}</p>
              <div className="text-xs text-gray-500 mt-2 space-y-0.5 border-t border-gray-200 pt-2">
                <p>Entitled: <span className="font-semibold text-gray-700">{b.entitled}</span></p>
                <p>Availed: <span className="font-semibold text-red-600">{b.availed}</span></p>
                <p>Adjusted: <span className="font-semibold text-green-700">{b.adjusted}</span></p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Leave Application History */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Leave Application History</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr>
                {['App No', 'Leave Type', 'From', 'To', 'Days', 'Status', 'Remarks'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {applications.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-4 text-sm font-semibold">{a.application_number}</td>
                  <td className="px-4 py-4 text-sm">{a.leave_type_name}</td>
                  <td className="px-4 py-4 text-sm">{a.from_date}</td>
                  <td className="px-4 py-4 text-sm">{a.to_date}</td>
                  <td className="px-4 py-4 text-sm font-bold">{a.no_of_days}</td>
                  <td className="px-4 py-4 text-sm uppercase font-semibold text-blue-600">{a.status}</td>
                  <td className="px-4 py-4 text-sm text-gray-500">{getRemarksDisplay(a)}</td>
                </tr>
              ))}
              {applications.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No leave applications found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-xl border border-gray-100 shadow-xl">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="font-bold text-gray-900">Apply Leave</h3>
              <button onClick={() => setShowApplyModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label><span className="text-sm font-semibold">Leave Type</span><select className={`${inputClass} mt-1`} value={form.leave_type_id} onChange={(e) => setForm((p) => ({ ...p, leave_type_id: e.target.value }))}>{leaveTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label>
              {/* <label className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 mt-6"><span className="text-sm font-semibold">Half Day</span><input type="checkbox" checked={form.is_half_day} onChange={(e) => setForm((p) => ({ ...p, is_half_day: e.target.checked }))} /></label> */}
              <label><span className="text-sm font-semibold">From Date</span><input type="date" className={`${inputClass} mt-1`} value={form.from_date} onChange={(e) => setForm((p) => ({ ...p, from_date: e.target.value }))} /></label>
              <label><span className="text-sm font-semibold">To Date</span><input type="date" className={`${inputClass} mt-1`} value={form.to_date} onChange={(e) => setForm((p) => ({ ...p, to_date: e.target.value }))} /></label>
              {form.is_half_day && <label><span className="text-sm font-semibold">Half Day Type</span><select className={`${inputClass} mt-1`} value={form.half_day_type} onChange={(e) => setForm((p) => ({ ...p, half_day_type: e.target.value }))}><option value="first_half">First Half</option><option value="second_half">Second Half</option></select></label>}
              <label className="md:col-span-2"><span className="text-sm font-semibold">Reason</span><textarea rows={4} className={`${inputClass} mt-1`} value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} /></label>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
              <button onClick={() => setShowApplyModal(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm">Cancel</button>
              <button onClick={submitLeave} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold">Submit Application</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeLeaves;
